import React, { useEffect, useState, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useSolanaBalances } from "@/hooks/use-solana-balances";
import { Loader2, ArrowLeft } from "lucide-react";
import { ethers } from "ethers";

// ============ MULTI-CHAIN TOKEN CONFIGURATION ============

const ALL_TOKENS = [
  "SOL", "USDT", "USDC", "FIXERCOIN", "PINGX", "LOCKER", "FXM", "JUP", "BONK", "PYTH", "ORCA", "RAY", "JTO", "MEW", "WIF", "POPCAT",
  "ETH", "BNB", "MATIC", "BTC", "DAI", "UNI", "LINK", "CAKE",
];

const TOKEN_CHAINS: Record<string, string> = {
  SOL: "solana", USDT: "solana", USDC: "solana", FIXERCOIN: "solana", PINGX: "solana", LOCKER: "solana", FXM: "solana",
  JUP: "solana", BONK: "solana", PYTH: "solana", ORCA: "solana", RAY: "solana", JTO: "solana", MEW: "solana", WIF: "solana", POPCAT: "solana",
  ETH: "ethereum", BNB: "bsc", MATIC: "polygon", BTC: "ethereum", DAI: "ethereum", UNI: "ethereum", LINK: "ethereum", CAKE: "bsc",
};

const TOKEN_ADDRESSES: Record<string, Record<string, string>> = {
  ethereum: {
    ETH: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
    USDT: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
    USDC: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    DAI: "0x6B175474E89094C44Da98b954EedeAC495271d0F",
    UNI: "0x1f9840a85d5af5bf1d1762f925bdaddc4201f984",
    LINK: "0x514910771AF9Ca656af840dff83E8264EcF986CA",
    BTC: "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599",
  },
  bsc: {
    BNB: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
    USDT: "0x55d398326f99059fF775485246999027B3197955",
    USDC: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d",
    CAKE: "0x0E09FaBB73Bd3Ade0a17ECC321fD13a19E81cE82",
  },
  polygon: {
    MATIC: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
    USDT: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F",
    USDC: "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174",
    DAI: "0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063",
  },
};

const RPC_URLS: Record<string, string> = {
  ethereum: "https://eth.llamarpc.com",
  bsc: "https://bsc-dataseed.binance.org/",
  polygon: "https://polygon-rpc.com",
};

const TOKEN_DECIMALS: Record<string, number> = {
  SOL: 9, USDT: 6, USDC: 6, FIXERCOIN: 6, PINGX: 6, LOCKER: 6, FXM: 6,
  JUP: 6, BONK: 5, PYTH: 6, ORCA: 6, RAY: 6, JTO: 6, MEW: 5, WIF: 6, POPCAT: 6,
  ETH: 18, BNB: 18, MATIC: 18, BTC: 8, DAI: 18, UNI: 18, LINK: 18, CAKE: 18,
};

const isValidEmail = (email: string) => /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/.test(email);
const isValidUserId = (userId: string) => /^[a-zA-Z0-9_-]{3,}$/.test(userId);

async function executeEVMTransfer(
  tokenSymbol: string,
  toAddress: string,
  amount: number,
  privateKey: string,
  chain: string
): Promise<string> {
  const provider = new ethers.JsonRpcProvider(RPC_URLS[chain]);
  const wallet = new ethers.Wallet(privateKey, provider);
  const decimals = TOKEN_DECIMALS[tokenSymbol] || 18;
  const amountInWei = ethers.parseUnits(amount.toString(), decimals);
  const tokenAddress = TOKEN_ADDRESSES[chain]?.[tokenSymbol];
  const isNativeToken = tokenAddress === "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";
  let tx;
  if (isNativeToken) {
    tx = await wallet.sendTransaction({ to: toAddress, value: amountInWei });
  } else {
    const erc20Abi = ["function transfer(address to, uint256 amount) returns (bool)"];
    const tokenContract = new ethers.Contract(tokenAddress, erc20Abi, wallet);
    tx = await tokenContract.transfer(toAddress, amountInWei);
  }
  await tx.wait();
  return tx.hash;
}

// ============ PASSWORD DIALOG ============
const PasswordDialog = ({ show, onClose, onSubmit, error, loading = false }) => {
  const [password, setPassword] = useState("");
  if (!show) return null;
  const handleSubmit = () => {
    if (password.trim() && !loading) {
      onSubmit(password);
      setPassword("");
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-bold text-foreground mb-4">Enter Your Password</h3>
        <p className="text-sm text-muted-foreground mb-4">Your password is required to decrypt your wallet and complete this transfer.</p>
        {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-500">{error}</div>}
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="mb-4 bg-background border-border text-foreground" onKeyPress={(e) => e.key === "Enter" && handleSubmit()} autoFocus disabled={loading} />
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border text-foreground hover:bg-muted" disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!password.trim() || loading} className="flex-1 bg-yellow-400 text-black hover:bg-yellow-500">{loading ? "Processing..." : "Confirm"}</Button>
        </div>
      </div>
    </div>
  );
};

// ============ BACKUP FILE DIALOG (ONLY PASTE OPTION) ============
const BackupFileDialog = ({ show, onClose, onSubmit, error, loading = false }) => {
  const [manualBackupText, setManualBackupText] = useState("");
  const [manualError, setManualError] = useState("");

  if (!show) return null;

  const validateBackupContent = (content: string): boolean => {
    if (content.length > 1024 * 1024) throw new Error("File too large. Maximum size is 1MB.");
    const parsed = JSON.parse(content);
    if (!parsed.magic || !parsed.salt || !parsed.iv || !parsed.data) throw new Error("Invalid backup file structure.");
    if (parsed.magic !== "FIXORIUM-MPC") throw new Error("Invalid backup file format.");
    return true;
  };

  const handleManualSubmit = () => {
    if (!manualBackupText.trim()) {
      setManualError("Please paste your backup JSON");
      return;
    }
    try {
      validateBackupContent(manualBackupText);
      onSubmit(manualBackupText);
      setManualBackupText("");
      setManualError("");
    } catch (err: any) {
      setManualError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full mx-4">
        {(error || manualError) && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-500">{error || manualError}</div>}
        
        <div className="space-y-3">
          <textarea
            value={manualBackupText}
            onChange={(e) => setManualBackupText(e.target.value)}
            placeholder='Paste your backup JSON here...'
            className="w-full h-32 bg-muted border border-border rounded-lg p-2 text-[8px] font-mono text-foreground resize-none focus:outline-none focus:border-yellow-400/50"
          />
          <Button onClick={handleManualSubmit} disabled={loading} className="w-full bg-yellow-400 text-black hover:bg-yellow-500 text-sm">
            Verify & Use
          </Button>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border text-foreground hover:bg-muted" disabled={loading}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ============ TRANSFER CARD ============
const TransferCard = ({
  user, token, setToken, toUserId, setToUserId, amount, setAmount,
  loading, balancesLoading, walletAddress, availableTokens,
  userBalance, handleTransferRequest, formatBalance,
  balances, navigate, checkingRecipient, recipientFound, onCheckRecipient,
  recipientError, selectedChain, isMobile
}: any) => (
  <div className={isMobile ? "w-full" : "bg-card rounded-xl p-6 h-full border border-border"}>
    <div className="mb-6">
      <h2 className="text-[12px] font-bold uppercase tracking-wider text-yellow-400">TRANSFER TOKEN</h2>
      <p className="text-[11px] text-muted-foreground mt-1 uppercase">Send tokens to another user</p>
    </div>
    <form onSubmit={handleTransferRequest} className="space-y-4">
      <div>
        <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-2">SELECT TOKEN <span className="text-[8px] text-muted-foreground">({selectedChain?.toUpperCase()})</span></label>
        {availableTokens.length === 0 ? (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
            <p className="text-[11px] text-red-500 uppercase">No tokens with sufficient balance</p>
            <p className="text-[11px] text-muted-foreground mt-1 uppercase">Minimum balance required: 0.00005 tokens</p>
          </div>
        ) : (
          <select value={token} onChange={(e) => setToken(e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-yellow-400 text-[10px] uppercase" disabled={loading}>
            {availableTokens.map((t: string) => (<option key={t} value={t} className="bg-background text-foreground">{t} ({formatBalance(balances?.[t] || 0)}) - {TOKEN_CHAINS[t]?.toUpperCase()}</option>))}
          </select>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-2">RECIPIENT (USER ID OR EMAIL)</label>
        <div className="relative">
          <Input value={toUserId} onChange={(e) => { setToUserId(e.target.value); onCheckRecipient(e.target.value); }} placeholder="Enter recipient user ID or email" className={`font-mono text-[10px] uppercase bg-background border-border text-foreground ${recipientFound ? 'border-green-500' : recipientError ? 'border-red-500' : ''}`} disabled={loading} />
          {checkingRecipient && (<div className="absolute right-3 top-1/2 transform -translate-y-1/2"><Loader2 className="w-4 h-4 animate-spin text-yellow-400" /></div>)}
        </div>
        {recipientFound && (<div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg"><p className="text-[8px] text-green-500 uppercase">✓ Recipient found: {recipientFound.email || recipientFound.id}</p>{recipientFound.wallets?.[selectedChain] && <p className="text-[6px] text-muted-foreground mt-1 truncate">Wallet: {recipientFound.wallets[selectedChain].slice(0, 20)}...</p>}</div>)}
        {recipientError && !checkingRecipient && toUserId && (<div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg"><p className="text-[8px] text-red-500 uppercase">✗ {recipientError}</p></div>)}
        <div className="text-[11px] text-muted-foreground mt-1 uppercase">Enter the user ID or email of the recipient</div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-2">AMOUNT</label>
        <div className="flex gap-2">
          <Input type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.0" className="font-mono text-[10px] bg-background border-border text-foreground" disabled={loading || availableTokens.length === 0} />
          <Button type="button" variant="outline" onClick={() => setAmount(userBalance.toString())} disabled={userBalance === 0 || loading || availableTokens.length === 0} className="text-[10px] uppercase border-border text-foreground hover:bg-muted">MAX</Button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-1 uppercase">Available: {formatBalance(userBalance)} {token}</div>
      </div>
      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={loading || balancesLoading || !walletAddress || availableTokens.length === 0 || !recipientFound} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-[10px] uppercase">{loading ? "PROCESSING..." : "SEND TRANSFER"}</Button>
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="flex-1 border border-border text-[10px] uppercase text-foreground hover:bg-muted" disabled={loading}>CANCEL</Button>
      </div>
    </form>
  </div>
);

// ============ MAIN COMPONENT ============
export default function Transfer() {
  const [user, setUser] = useState<any | null>(null);
  const [token, setToken] = useState<string>("SOL");
  const [toUserId, setToUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [backupFileContent, setBackupFileContent] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState("");
  const [pendingTransfer, setPendingTransfer] = useState<any>(null);
  const [recipientFound, setRecipientFound] = useState<any>(null);
  const [recipientError, setRecipientError] = useState<string>("");
  const [checkingRecipient, setCheckingRecipient] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [selectedChain, setSelectedChain] = useState<string>("solana");
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🔐 SECURE USER LOADING using HttpOnly cookie
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            return;
          }
        }
        navigate("/login");
      } catch (error) {
        console.error("[Transfer] Auth error:", error);
        navigate("/login");
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (token) {
      const chain = TOKEN_CHAINS[token];
      setSelectedChain(chain || "solana");
    }
  }, [token]);


  const getWalletAddress = useCallback(() => {
    if (!user?.wallets) return "";
    switch (selectedChain) {
      case "solana": return user.wallets.solana || "";
      case "ethereum": return user.wallets.ethereum || "";
      case "bsc": return user.wallets.bsc || "";
      case "polygon": return user.wallets.polygon || "";
      default: return "";
    }
  }, [user, selectedChain]);

  const walletAddress = getWalletAddress();
  const { balances: solanaBalances, loading: balancesLoading, refetch: refetchBalances } = useSolanaBalances({
    walletAddress: selectedChain === "solana" ? walletAddress : "",
    refetchInterval: 15000,
    enabled: selectedChain === "solana" && !!walletAddress,
  });
  const balances = solanaBalances;

  const availableTokens = ALL_TOKENS.filter(tokenSymbol => {
    const tokenChain = TOKEN_CHAINS[tokenSymbol];
    if (tokenChain !== selectedChain) return false;
    const balance = balances?.[tokenSymbol] || 0;
    return balance >= 0.00005;
  });
  
  const userBalance = balances?.[token] || 0;

  useEffect(() => {
    if (availableTokens.length > 0 && !availableTokens.includes(token)) setToken(availableTokens[0]);
  }, [availableTokens, token]);

  const isValidIdentifier = (input: string) => input && input.length >= 3 && (isValidEmail(input) || isValidUserId(input));

  const checkRecipient = useCallback(async (identifier: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!identifier || identifier.trim().length < 3) {
      setRecipientFound(null); setRecipientError(""); setCheckingRecipient(false);
      return;
    }
    if (!isValidIdentifier(identifier)) {
      setRecipientFound(null); setRecipientError(""); setCheckingRecipient(false);
      return;
    }
    if (identifier === user?.id || identifier === user?.email) {
      setRecipientFound(null); setRecipientError("You cannot transfer to yourself"); setCheckingRecipient(false);
      return;
    }
    debounceTimerRef.current = setTimeout(async () => {
      setCheckingRecipient(true);
      setRecipientError("");
      try {
        const response = await fetch(`/api/auth?action=findUser&identifier=${encodeURIComponent(identifier)}`, {
          credentials: "include",
        });
        const data = await response.json();
        if (response.status === 404 || !data.success) {
          setRecipientFound(null);
          setRecipientError("User not found");
          return;
        }
        const foundUser = data.user;
        const recipientWallet = foundUser.wallets?.[selectedChain];
        if (!recipientWallet) {
          setRecipientFound(null);
          setRecipientError(`Recipient does not have a ${selectedChain.toUpperCase()} wallet`);
          return;
        }
        setRecipientFound(foundUser);
        setRecipientError("");
      } catch (error) {
        console.error("[Transfer] Error checking recipient:", error);
        setRecipientFound(null);
        setRecipientError("Failed to check recipient");
      } finally {
        setCheckingRecipient(false);
      }
    }, 800);
  }, [user, selectedChain]);

  useEffect(() => {
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
  }, []);

  const sendTransaction = async (
    fromUserId: string,
    toUser: any,
    tokenSymbol: string,
    amountNum: number,
    password: string,
    backupFile?: string
  ): Promise<{ success: boolean; txHash?: string; error?: string }> => {
    try {
      const recipientWallet = toUser.wallets?.[selectedChain];
      const chain = TOKEN_CHAINS[tokenSymbol];
      if (chain === "solana") {
        const response = await fetch("/api/wallet?action=withdraw", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            userId: fromUserId,
            password,
            amount: amountNum,
            to_address: recipientWallet,
            token: tokenSymbol,
            ...(backupFile ? { backupFile } : {}),
          }),
        });
        const result = await response.json();
        if (response.status === 401) throw new Error("Invalid password");
        if (!response.ok) throw new Error(result.error || result.message || "Transfer failed");
        return { success: true, txHash: result.transactionHash || result.transactionId };
      } else if (chain === "ethereum" || chain === "bsc" || chain === "polygon") {
        const response = await fetch("/api/transfer-evm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            action: "transfer",
            chain,
            token: tokenSymbol,
            toAddress: recipientWallet,
            amount: amountNum,
            userId: fromUserId,
            password,
            ...(backupFile ? { backupFile } : {}),
          }),
        });
        const result = await response.json();
        if (response.status === 401) throw new Error("Invalid password");
        if (!response.ok) throw new Error(result.error || "Transfer failed");
        if (result.success && result.txHash) return { success: true, txHash: result.txHash };
        else if (result.success && result.tx && result.privateKey) {
          const txHash = await executeEVMTransfer(tokenSymbol, recipientWallet, amountNum, result.privateKey, chain);
          return { success: true, txHash };
        } else throw new Error(result.error || "Transfer failed");
      } else throw new Error(`Unsupported chain: ${chain}`);
    } catch (error: any) {
      console.error("[Transfer] Transaction failed:", error);
      return { success: false, error: error.message || "Transfer failed" };
    }
  };

  const handleTransferRequest = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!user) { toast({ title: "ERROR", description: "User not authenticated" }); navigate("/login"); return; }
    if (!toUserId || !amount || !token) { toast({ title: "MISSING", description: "Please fill all fields" }); return; }
    if (!recipientFound) { toast({ title: "INVALID RECIPIENT", description: recipientError || "Please enter a valid user ID or email" }); return; }
    const num = Number(amount);
    if (isNaN(num) || num <= 0) { toast({ title: "INVALID AMOUNT", description: "Please enter a valid amount" }); return; }
    
    if (num > userBalance) {
      toast({ title: "INSUFFICIENT BALANCE", description: `You have ${userBalance.toFixed(6)} ${token} available` });
      return;
    }


    setPendingTransfer({ recipient: recipientFound, amount: num, token });
    if (user.isMPC && !backupFileContent) {
      setShowBackupDialog(true);
    } else {
      setShowPasswordDialog(true);
      setPasswordError("");
    }
  };

  const handleBackupSubmit = (backupContent: string) => {
    setBackupFileContent(backupContent);
    setShowBackupDialog(false);
    setShowPasswordDialog(true);
    setPasswordError("");
  };

  const handlePasswordSubmit = async (enteredPassword: string) => {
    if (!enteredPassword?.trim()) { setPasswordError("Please enter your password"); return; }
    if (!pendingTransfer || !user) return;
    setLoading(true);
    setPasswordError("");
    try {
      const result = await sendTransaction(
        user.id || user.email,
        pendingTransfer.recipient,
        pendingTransfer.token,
        pendingTransfer.amount,
        enteredPassword,
        backupFileContent || undefined
      );
      if (result.success) {

        toast({ title: "SUCCESS", description: `Successfully sent ${pendingTransfer.amount} ${pendingTransfer.token} to ${pendingTransfer.recipient.email || pendingTransfer.recipient.id}\nTx: ${result.txHash?.slice(0, 20)}...` });
        setAmount("");
        setToUserId("");
        setRecipientFound(null);
        setRecipientError("");
        setShowPasswordDialog(false);
        setPendingTransfer(null);
        setTimeout(() => refetchBalances(), 3000);
      } else throw new Error(result.error);
    } catch (err: any) {
      const errorMessage = err?.message || "Unable to complete transfer";
      setPasswordError(errorMessage);
      toast({ title: "TRANSFER FAILED", description: errorMessage, variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleCancelPassword = () => {
    setShowPasswordDialog(false);
    setPendingTransfer(null);
    setPasswordError("");
  };

  const handleCancelBackup = () => {
    setShowBackupDialog(false);
    setPendingTransfer(null);
  };

  const formatBalance = (balance: number) => {
    if (balance === 0) return "0";
    if (balance < 0.000001) return balance.toExponential(4);
    if (balance < 0.001) return balance.toFixed(8);
    if (balance < 1) return balance.toFixed(6);
    return balance.toFixed(4);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {isDesktop ? (
        // DESKTOP: single centered column
        <div className="p-6 min-h-screen flex justify-center">
          <div className="w-full max-w-xl">
            <TransferCard
              user={user} token={token} setToken={setToken} toUserId={toUserId} setToUserId={setToUserId}
              amount={amount} setAmount={setAmount} loading={loading} balancesLoading={balancesLoading}
              walletAddress={walletAddress} availableTokens={availableTokens} userBalance={userBalance}
              handleTransferRequest={handleTransferRequest} formatBalance={formatBalance} balances={balances}
              navigate={navigate} checkingRecipient={checkingRecipient} recipientFound={recipientFound}
              onCheckRecipient={checkRecipient} recipientError={recipientError} selectedChain={selectedChain}
              isMobile={false}
            />
          </div>
        </div>
      ) : (
        <div className="px-[9px] py-6 min-h-screen">
          <div className="w-full">
            <TransferCard
              user={user} token={token} setToken={setToken} toUserId={toUserId} setToUserId={setToUserId}
              amount={amount} setAmount={setAmount} loading={loading} balancesLoading={balancesLoading}
              walletAddress={walletAddress} availableTokens={availableTokens} userBalance={userBalance}
              handleTransferRequest={handleTransferRequest} formatBalance={formatBalance} balances={balances}
              navigate={navigate} checkingRecipient={checkingRecipient} recipientFound={recipientFound}
              onCheckRecipient={checkRecipient} recipientError={recipientError} selectedChain={selectedChain}
              isMobile={true}
            />
          </div>
        </div>
      )}
      <PasswordDialog show={showPasswordDialog} onClose={handleCancelPassword} onSubmit={handlePasswordSubmit} error={passwordError} loading={loading} />
      <BackupFileDialog show={showBackupDialog} onClose={handleCancelBackup} onSubmit={handleBackupSubmit} error="" loading={false} />
    </div>
  );
}
